<template>
  <div id="welcome">
    <div id="help">
      <v-dialog
        v-model="helpDialog"
        fullscreen
        hide-overlay
        transition="dialog-bottom-transition"
      >
        <template v-slot:activator="{ on }">
          <v-btn icon color="error" v-on="on">
            <v-icon>mdi-help-circle</v-icon></v-btn
          >
        </template>
        <Help :closeDialog="closeHelp" />
      </v-dialog>
    </div>
    <div id="entity">
      <h1>{{ entityName }}</h1>
      <v-img id="logo" v-bind:src="logoUrl"> </v-img>
      <br />
    </div>
    <div v-if="isRoomTimingsViewEnabled">
      <b>{{ roomName }}</b>
      <br /><br />
      <b>{{ $t('message.waiting.date') }}</b
      >: {{ getDate(startDate) }}
      <br />
      <b>{{ $t('message.waiting.startTime') }}</b
      >: {{ getHour(startDate) }}
      <br />
      <b>{{ $t('message.waiting.endTime') }}</b
      >: {{ getHour(endDate) }}
    </div>
    <div v-else>
      <b>{{ roomName }}</b>
      <br /><br />
      <b>{{ $t('message.waiting.date') }}</b
      >: {{ getDate(startDate) }}
      <br />
      <br />
    </div>
    <div>
      <!-- <v-btn v-on:click="debug" rounded color="orange">Debug</v-btn> -->
      <div id="join-button" v-if="!isRoomEnded && isBrowserCompatible()">
        <v-btn
          v-bind:disabled="
            connectionStatus.current === 'disconnected' ||
              !isCallInfoValid ||
              !isRoomInTime ||
              !isPreJoinned
          "
          v-on:click="joinCall"
          rounded
          color="green"
          >{{ $t('message.waiting.join') }}</v-btn
        >
      </div>
      <div id="delay-notification" v-if="canNotify">
        <v-dialog
          v-model="delayNotificationDialog"
          fullscreen
          hide-overlay
          transition="dialog-bottom-transition"
        >
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-btn
                rounded
                color="red"
                v-bind:disabled="
                  connectionStatus.current === 'disconnected' ||
                    !isCallInfoValid ||
                    !isRoomInTime ||
                    !isPreJoinned
                "
              >
                {{ $t('message.waiting.delayRoom') }}
              </v-btn>
            </div>
          </template>
          <DelayNotification
            :sendDelayNotification="notifyDelay"
            :closeDialog="closeDelayNotification"
          />
        </v-dialog>
      </div>
    </div>
    <div style="clear: both;" />
    <div v-if="!isRoomEnded">
      <div v-if="isBrowserCompatible()">
        <v-alert
          icon="mdi-alert"
          border="left"
          colored-border
          type="warning"
          elevation="2"
        >
          {{ $t('message.waiting.welcomeAlert') }}
        </v-alert>
      </div>
      <div v-if="!isBrowserCompatible()">
        <v-alert
          icon="mdi-alert-octagon"
          border="left"
          colored-border
          type="error"
          elevation="2"
        >
          {{ $t('message.waiting.compatibilityAlert') }}
        </v-alert>
      </div>
    </div>
    <div v-if="isRoomEnded">
      <v-alert
        icon="mdi-alert"
        border="left"
        colored-border
        type="error"
        elevation="2"
      >
        {{ $t('message.waiting.roomEnded', { role: finishedBy }) }}
      </v-alert>
    </div>
  </div>
</template>

<script>
import Help from '@/views/Help.vue';
import DelayNotification from '@/components/DelayNotification.vue';

export default {
  props: {
    /* Información del participante y sala */
    userId: String,
    roomId: String,
    token: String,
    /* Información de la sala */
    entityName: String,
    logoUrl: String,
    role: String,
    roomName: String,
    startTimestamp: Number,
    endTimestamp: Number,
    roomFinishedBy: Number,
    // Entrada directa habilitada? (entrar directamente en vídeo si se puede)
    isDirectJoiningEnabled: Boolean,
    // Indica si se deben mostrar los horarios de la sala
    isRoomTimingsViewEnabled: Boolean,
    isRoomEnded: Boolean,
    /* Estado actual de la app, de la conexión y de la info de la sala */
    appStatus: String,
    connectionStatus: { type: Object, default: () => ({}) },
    isCallInfoValid: Boolean,
    /* Callbacks */
    getCallInfo: { type: Function }, // CHECKME: AQUI PARA QUE????
    onStartCall: { type: Function },
    showMessage: { type: Function },
    showMessagePermanent: { type: Function }
  },
  components: {
    Help,
    DelayNotification
  },
  data: () => ({
    // Dialogos de ayuda y notificación de retraso
    helpDialog: false,
    delayNotificationDialog: false,
    // Indica si ya ha llegado el inicio de sesión programado para la sala
    isRoomInTime: false,
    // Intervalo de tiempo para comprobar si llegó el inicio de sesión de la sala
    roomInTimeChecker: undefined,
    // Indica si ya se hizo prejoin
    isPreJoinned: false
  }),
  async created() {
    if (!this.isRoomEnded) {
      // Hacemos el preJoin aquí para que pueda recibir las delayNotification
      //this.preJoinRoom();
      // Comprobamos si la sala ya está habilitada
      this.updateRoomInTime();
      // Creamos una tarea a intervalos para comprobar si la sala se habilitada
      if (!this.roomInTimeChecker && !this.isRoomInTime) {
        this.roomInTimeChecker = setInterval(() => {
          this.updateRoomInTime();
        }, 2000);
      }
      if (this.connectionStatus.current === 'connected' && !this.isRoomEnded) {
        this.preJoinRoom();
      } else {
        this.isPreJoinned = false;
      }
    } else {
      console.log(`Waiting: La sala '${this.roomId}' ya ha finalizado.`);
    }
  },
  computed: {
    startDate: function() {
      return new Date(this.startTimestamp * 1000);
    },
    endDate: function() {
      return new Date(this.endTimestamp * 1000);
    },
    canNotify: function() {
      return this.role === 'doctor' && !this.isRoomEnded;
    },
    finishedBy: function() {
      if (this.isRoomEnded) {
        if (this.roomFinishedBy === 0) {
          // Closed by system (halted)
          return this.$t('message.videoCall.system');
        } else if (this.roomFinishedBy == this.userId) {
          // Closed by us
          return this.$t(`message.videoCall.${this.role}`);
        } else {
          // Not closed by us
          if (this.role === 'doctor') {
            // Not closed by system nor doctor, must be patient
            return this.$t('message.videoCall.patient');
          } else if (this.role === 'patient') {
            // Not closed by system nor patient, must be doctor
            return this.$t('message.videoCall.doctor');
          } else {
            // CHECKME: In guest we can't know with current available data
            return this.$t('message.videoCall.doctor');
          }
        }
      }
      return '';
    }
  },
  sockets: {
    delayNotification: function(notification) {
      const minutes = notification.minutes;
      const message = this.getDelayNotificationMessage(minutes);
      this.showMessagePermanent(message);
    }
  },
  watch: {
    'connectionStatus.current': async function() {
      console.log(
        `Waiting: La conexión cambió de estado (${this.connectionStatus.current})`
      );
      // Hack para forzar la desconexión del participante en la API y volver
      // a unirlo
      // CHECKME: REALMENTE ES NECESARIO AQUI??
      this.$socket.client.emit('unableToConnectVideo', this.roomId);
      await new Promise(r => setTimeout(r, 3000));

      if (this.connectionStatus.current === 'connected' && !this.isRoomEnded) {
        this.preJoinRoom();
      } else {
        this.isPreJoinned = false;
      }
    },
    isPreJoinned: function() {
      if (this.isPreJoinned) {
        // Entrada directa a sala para paciente y acompañante
        if (
          // Condiciones de entrada directa a sala
          this.isDirectJoiningEnabled &&
          this.role !== 'doctor' &&
          // Condiciones para poder entrar a la sala
          this.isRoomInTime &&
          !this.isRoomEnded
        ) {
          console.log(
            `Waiting: Ejecutando entrada directa a sala '${this.roomId}' como ${this.role}`
          );
          this.joinCall();
        } else {
          console.log(`Waiting: Esperando entrada a sala '${this.roomId}'.`);
        }
      }
    }
  },
  methods: {
    debug() {
      console.dir(this);
    },
    closeHelp() {
      this.helpDialog = false;
    },
    closeDelayNotification() {
      this.delayNotificationDialog = false;
    },
    // Comprueba si ha llegado el momento de iniciar la sesión
    updateRoomInTime() {
      // Calculamos si hemos llegado a la fecha de inicio de la sesión
      const currentDate = new Date().getTime();
      const startDate = this.startDate.getTime();
      this.isRoomInTime = currentDate >= startDate;
      // Quitamos el timer de comprobación a intervalos
      if (this.isRoomInTime) {
        console.log(`Waiting: Sala '${this.roomId}' en tiempo de sesión`);
        clearInterval(this.roomInTimeChecker);
      }
    },
    getHour(date) {
      return date.toLocaleTimeString();
    },
    getDate(date) {
      return (
        date.getDate() + '/' + (date.getMonth() + 1) + '/' + date.getFullYear()
      );
    },
    isBrowserCompatible() {
      if (navigator.mediaDevices) {
        return true;
      }
      return false;
    },
    preJoinRoom() {
      console.log(`Waiting: Ejecutando pre-entrada en sala '${this.roomId}'`);
      this.$socket.client.emit(
        'preJoinRoom',
        {
          userId: this.userId,
          roomId: this.roomId,
          token: this.token
        },
        response => {
          if (!response) {
            // Normalmente se da con "User already in room"
            // Aunque puede darse Invalid request si los datos enviados no son
            // válidos. Asumimos que siempre son válidos
            console.log(
              `Waiting: El participante ya pre-entro en sala '${this.roomId}'`
            );
            this.isPreJoinned = true;
          } else if (!response.success) {
            // ----------------------------------------------------------------
            // CHECKME: ESTE CASO ES COMPLEJO, YA QUE SI LA SALA ESTÁ LLENA
            // SIMPLEMENTE NO LE DEJA ENTRAR Y NO PUEDE HACER NADA POR SOLUCIONARLO
            // ----------------------------------------------------------------
            // Posibles response.response "Invalid request", "Room is full"
            console.log(
              `Waiting: Error pre-entrando en sala '${this.roomId}': ${response.response}`
            );
            // Hack para intentar forzar la desconexión del participante en la
            // API y tratar de volver a unirlo
            this.$socket.client.emit('unableToConnectVideo', this.roomId);
            this.isPreJoinned = false;
            // CHECKME: ESTO LE PUEDE MOSTRAR AL USUARIO UN MENSAJE EXTRAÑO!!
            this.showMessage(response.message);
          } else {
            console.log('Waiting: Pre-entrada en sala correcta');
            this.isPreJoinned = true;
            if (response.response.roomDelayed) {
              const minutes = response.response.minutes;
              const message = this.getDelayNotificationMessage(minutes);
              this.showMessagePermanent(message);
            }
          }
        }
      );
    },
    async joinCall() {
      // CHECKME: Agregar el id de participante y el role
      console.log(`Waiting: Participante entrando en sala`);
      // Actualizamos los datos de la sala desde API antes de entrar en la sala
      await this.getCallInfo();
      if (this.isRoomEnded) {
        // Después de actualizar los datos, la sala está finalizada
        this.showMessage(
          this.$t('message.waiting.roomEnded', { role: this.finishedBy })
        );
      } else if (!this.isCallInfoValid) {
        // La conexión parece haberse perdido durante la pulsación del botón
        this.showMessage(this.$t('message.waiting.connectionMaybeLost'));
      } else if (this.isCallInfoValid) {
        // Sala no finalizada y sabemos que la info de la sala es correcta
        this.onStartCall();
      }
    },
    async notifyDelay(minutes) {
      // Actualizamos los datos de la sala desde API antes de entrar en la sala
      await this.getCallInfo();
      if (this.isRoomEnded) {
        // Después de actualizar los datos, la sala está finalizada
        this.showMessage(
          this.$t('message.waiting.roomEnded', { role: this.finishedBy })
        );
      } else if (!this.isCallInfoValid) {
        // La conexión parece haberse perdido durante la pulsación del botón
        this.showMessage(this.$t('message.waiting.connectionMaybeLost'));
      } else if (this.isCallInfoValid) {
        this.$socket.client.emit('delayNotification', {
          room: this.roomId,
          minutes: minutes,
          userId: this.userId,
          roomId: this.roomId,
          token: this.token
        });
        this.closeDelayNotification();
        this.showMessage(this.$t('message.delayNotification.delayMessageSent'));
      }
    },
    getDelayNotificationMessage(minutes) {
      return this.$t('message.delayNotification.doctorDelayMessage', {
        minutes
      });
    }
  }
};
</script>

<style>
#welcome {
  padding: 3vh;
  transition: all 1s;
  height: 100vh;
  display: flex;
  display: webkit-flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
}

#entity {
  text-align: center;
}

#logo {
  max-width: 180px;
  width: 50vw;
  display: block;
  margin: 0 auto;
}

#help {
  position: absolute;
  top: 3px;
  right: 3px;
}

#join-button {
  float: left;
}

#delay-notification {
  margin-left: 5px;
  float: left;
}

#join-button {
  float: left;
}

#delay-notification {
  margin-left: 5px;
  float: left;
}
</style>
